import { useMemo, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import {
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
  Columns3, Download, Search, X,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FilterBar } from './FilterBar'
import { EmptyState } from './EmptyState'
import { ErrorState } from './ErrorState'
import type { PageResponse } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { Select } from '@/components/ui/select'

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[]
  data: PageResponse<T> | undefined
  isLoading?: boolean
  error?: unknown
  onRetry?: () => void

  /** Server-side state, owned by the caller so it can live in the URL. */
  page: number
  size: number
  sort?: string
  search?: string
  onPageChange: (page: number) => void
  onSizeChange?: (size: number) => void
  onSortChange?: (sort: string | undefined) => void
  onSearchChange?: (search: string) => void

  /** Enables the checkbox column and the bulk-action bar. */
  enableSelection?: boolean
  bulkActions?: (selectedIds: string[], clear: () => void) => ReactNode
  getRowId?: (row: T) => string

  /**
   * What clicking a row opens.
   *
   * <p>A grid where only one cell is a link is a grid people click at and nothing happens
   * on. Keyboard users get the same thing: the row takes focus and Enter follows it.
   */
  onRowClick?: (row: T) => void

  onExport?: (format: 'csv' | 'xlsx' | 'pdf') => void
  /**
   * Columns hidden until somebody turns them on in the chooser.
   *
   * <p>For the columns a screen should not open with — a directory's contact details, say.
   * They are still there, one menu away, which is different from not offering them.
   */
  initialHiddenColumns?: string[]
  toolbar?: ReactNode
  emptyTitle?: string
  emptyDescription?: string
  className?: string
}

const PAGE_SIZES = [10, 20, 50, 100]

/**
 * The single table implementation in the app.
 *
 * Everything is **server-side** — pagination, sorting, filtering and search all round-trip
 * to the API — because HR tables routinely run to tens of thousands of rows and a
 * client-side table would have to load all of them to sort one column.
 *
 * No feature page may build its own table; an ESLint rule bans `useReactTable` outside
 * this directory, which is what keeps "a new CRUD page is three pieces of config" true.
 */
export function DataTable<T>({
  columns,
  data,
  isLoading,
  error,
  onRetry,
  page,
  size,
  sort,
  search,
  onPageChange,
  onSizeChange,
  onSortChange,
  onSearchChange,
  enableSelection,
  bulkActions,
  getRowId,
  onExport,
  onRowClick,
  initialHiddenColumns,
  toolbar,
  emptyTitle = 'No records found',
  emptyDescription,
  className,
}: DataTableProps<T>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    Object.fromEntries((initialHiddenColumns ?? []).map((id) => [id, false])))
  const [columnChooserOpen, setColumnChooserOpen] = useState(false)

  // Mirror the server sort string into TanStack's shape so headers show the right arrow.
  const sorting = useMemo<SortingState>(() => {
    if (!sort) return []
    const [id, direction] = sort.split(',')
    return id ? [{ id, desc: direction === 'desc' }] : []
  }, [sort])

  const table = useReactTable({
    data: data?.content ?? [],
    columns,
    state: { sorting, rowSelection, columnVisibility },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: data?.totalPages ?? 0,
    enableRowSelection: Boolean(enableSelection),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
  })

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id])
  const clearSelection = () => setRowSelection({})

  function toggleSort(columnId: string) {
    if (!onSortChange) return
    const current = sorting[0]
    if (current?.id !== columnId) return onSortChange(`${columnId},asc`)
    if (!current.desc) return onSortChange(`${columnId},desc`)
    return onSortChange(undefined) // third click clears
  }

  const rows = table.getRowModel().rows
  const showEmpty = !isLoading && !error && rows.length === 0

  return (
    <div className={cn('hrms-card overflow-hidden', className)}>
      {/* ---------- toolbar ---------- */}
      {/*
       * The same row every list screen uses — search, filters, actions — so a table here
       * and a hand-built list two screens away line up.
       */}
      <FilterBar
        className="border-b border-[var(--color-border)] p-3"
        search={onSearchChange && (
          <div className="relative">
            <Search
              className="pointer-events-none absolute start-0 top-1/2 ms-3 size-4 -translate-y-1/2 text-[var(--color-muted)]"
              aria-hidden="true"
            />
            <Input
              value={search ?? ''}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search..."
              aria-label="Search table"
              className="ps-9"
            />
          </div>
        )}
        actions={
          <>
          {onExport && (
            <Button variant="outline" size="sm" onClick={() => onExport('csv')}>
              <Download aria-hidden="true" />
              Export
            </Button>
          )}
          <DropdownMenu open={columnChooserOpen} onOpenChange={setColumnChooserOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Columns3 aria-hidden="true" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Columns</DropdownMenuLabel>
              {table.getAllLeafColumns()
                .filter((column) => column.id !== 'select' && column.id !== 'actions')
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    // The menu stays open so several columns can be toggled in one go.
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(checked) => column.toggleVisibility(Boolean(checked))}
                  >
                    {String(column.columnDef.header ?? column.id)}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </>
        }
      >
        {toolbar}
      </FilterBar>

      {/* ---------- bulk actions ---------- */}
      {enableSelection && selectedIds.length > 0 && (
        <div
          role="region"
          aria-live="polite"
          className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-primary-soft)] px-4 py-2"
        >
          <span className="text-sm font-medium text-[var(--color-primary)]">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            {bulkActions?.(selectedIds, clearSelection)}
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X aria-hidden="true" />
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* ---------- table ---------- */}
      <div className="overflow-x-auto overscroll-x-contain">
        <table data-slot="data-table" className="w-full border-collapse text-sm">
          <thead className="bg-[var(--color-page)]">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {enableSelection && (
                  <th scope="col" className="w-10 px-3 py-2.5">
                    <input
                      type="checkbox"
                      aria-label="Select all rows on this page"
                      checked={table.getIsAllRowsSelected()}
                      ref={(element) => {
                        if (element) element.indeterminate = table.getIsSomeRowsSelected()
                      }}
                      onChange={table.getToggleAllRowsSelectedHandler()}
                      className="accent-[var(--color-primary)]"
                    />
                  </th>
                )}
                {headerGroup.headers.map((header) => {
                  const sortable = header.column.getCanSort() && Boolean(onSortChange)
                  const active = sorting[0]?.id === header.column.id
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={
                        active ? (sorting[0]?.desc ? 'descending' : 'ascending') : 'none'
                      }
                      className="px-3 py-2.5 text-start text-xs font-semibold text-[var(--color-body)]"
                    >
                      {header.isPlaceholder ? null : sortable ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(header.column.id)}
                          className="inline-flex items-center gap-1 hover:text-[var(--color-primary)]"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {!active && <ArrowUpDown className="size-3 opacity-50" aria-hidden="true" />}
                          {active && !sorting[0]?.desc && <ArrowUp className="size-3" aria-hidden="true" />}
                          {active && sorting[0]?.desc && <ArrowDown className="size-3" aria-hidden="true" />}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {isLoading &&
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="border-t border-[var(--color-border)]">
                  {Array.from({ length: columns.length + (enableSelection ? 1 : 0) }).map(
                    (__, cellIndex) => (
                      <td key={`skeleton-cell-${cellIndex}`} className="px-3 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ),
                  )}
                </tr>
              ))}

            {!isLoading &&
              rows.map((row) => (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  onKeyDown={onRowClick
                    ? (event) => {
                      if (event.key === 'Enter') onRowClick(row.original)
                    }
                    : undefined}
                  className={cn(
                    'border-t border-[var(--color-border)] hover:bg-[var(--color-page)]',
                    'data-[state=selected]:bg-[var(--color-primary-soft)]',
                    onRowClick && 'cursor-pointer',
                  )}
                >
                  {enableSelection && (
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        aria-label="Select row"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        className="accent-[var(--color-primary)]"
                      />
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-2.5 text-[var(--color-heading)]">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>

        {error != null && <ErrorState error={error} onRetry={onRetry} />}
        {showEmpty && <EmptyState title={emptyTitle} description={emptyDescription} />}
      </div>

      {/* ---------- pagination ---------- */}
      {data && data.totalElements > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border)] px-4 py-3 text-sm">
          <p className="text-[var(--color-muted)]">
            {page * size + 1}–{Math.min((page + 1) * size, data.totalElements)} of{' '}
            {data.totalElements}
          </p>

          <div className="flex items-center gap-3">
            {onSizeChange && (
              <label className="flex items-center gap-1.5 text-[var(--color-muted)]">
                Rows
                <Select
                  value={size}
                  onChange={(event) => onSizeChange(Number(event.target.value))}
                >
                  {PAGE_SIZES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </label>
            )}

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                aria-label="Previous page"
                disabled={data.first}
                onClick={() => onPageChange(page - 1)}
              >
                <ChevronLeft className="rtl-flip" aria-hidden="true" />
              </Button>
              <span className="px-2 tabular-nums text-[var(--color-body)]">
                {page + 1} / {Math.max(data.totalPages, 1)}
              </span>
              <Button
                variant="outline"
                size="icon"
                aria-label="Next page"
                disabled={data.last}
                onClick={() => onPageChange(page + 1)}
              >
                <ChevronRight className="rtl-flip" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
